import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Typography } from '@mui/material';
import React, { type ReactNode, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useHover } from 'usehooks-ts';

import { useCheckPermissions } from '../../auth/index.js';
import { type Camera, type Patient } from '../../models/index.js';
import { isLayoutEditing } from '../../redux/selectors.js';
import { Events, track, useEventSource, type WithRequired } from '../../utils/index.js';
import ActivityEventToast from '../Activity/ActivityEventToast.js';
import LivestreamHls from '../LivestreamHls.js';
import StreamControls from './StreamControls.js';
import useHQ from '../../hooks/use-HQ.js';
import RouterNetworkBadge from '../Router/RouterNetworkBadge.js';
import StreamOffMessage from '../StreamOffMessage.js';

interface StreamContainerProps {
  onClick: (patientId: string) => void;
  inactivity: boolean;
  patient: Patient;
}

interface MotionEvent {
  cameraName: string;
  cameraId: string;
  patientId: string;
  motion: boolean;
}

const audioNotification = new Audio('/notification--for-sure.mp3');

const StreamContainer: FC<StreamContainerProps> = ({ onClick, inactivity, patient }) => {
  const eventSource = useEventSource(`${import.meta.env.VITE_API_BASE_URL}/streams/sse/${patient.id}`, {
    shouldListen: true,
  });

  const [subtitle, setSubtitle] = useState<string | ReactNode>('');
  const [cameraId, setCameraId] = useState<string>('');
  const [lastActivity, setLastActivity] = useState<string>();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const { isHQ, setIsHQ } = useHQ(cameraId);
  const { canReadActivityEvent } = useCheckPermissions();

  const cameraIdRef = useRef<string | undefined>();
  const livestreamWithControlsRef = useRef<HTMLDivElement>(null);
  const isHovered = useHover(livestreamWithControlsRef);

  const getMostRecentActiveCamera = (cameras: Camera[]): Camera => {
    const camerasWithActivity = cameras.filter((camera) => camera.lastActivity);

    if (camerasWithActivity.length === 0) {
      return cameras[0];
    }

    return camerasWithActivity.reduce((mostRecent, camera) => {
      const isMostRecent = new Date(camera.lastActivity) > new Date(mostRecent.lastActivity);
      return isMostRecent ? camera : mostRecent;
    });
  };

  useEffect(() => {
    if (patient?.cameras && patient?.cameras.length > 0) {
      const lastActivityCamera = getMostRecentActiveCamera(patient.cameras);
      setCamera({ id: lastActivityCamera.id, name: lastActivityCamera.name, lastActivity: lastActivityCamera.lastActivity });
    }
  }, [patient]);

  useEffect(() => {
    const onMessage = (message: MessageEvent) => {
      const { cameraId: newCameraId, motion, cameraName } = JSON.parse(message.data) as MotionEvent;
      // Only plays notifications when this stream first comes on
      if (motion && newCameraId !== cameraIdRef.current) {
        // onMotion
        setIsHighlighted(true);

        setTimeout(() => {
          setIsHighlighted(false);
        }, 20 * 1000); // 20s of highlighting

        audioNotification.play();

        track(Events.CameraMotionView, {
          cameraId,
        });
      }
      setCamera({ id: newCameraId, name: cameraName });
    };

    // message = when an event is received from the server
    eventSource?.addEventListener('message', onMessage);
    // error = when an SSE-related error is received from the server
    eventSource?.addEventListener('error', function (err) {
      console.error('error', err);
    });
    // clean up
    return () => {
      eventSource?.removeEventListener('message', onMessage);
      eventSource?.removeEventListener('error', function (err) {
        console.error('error', err);
      });
    };
  }, [eventSource?.readyState]);

  const onPause = () => {
    setIsPlaying(false);

    track(Events.LivestreamPause, {
      cameraId,
      patientId: patient.id,
    });
  };

  const onPlay = () => {
    setIsPlaying(true);

    track(Events.LivestreamPlay, {
      cameraId,
      patientId: patient.id,
    });
  };

  const setCamera = ({ id, name, lastActivity }: WithRequired<Partial<Camera>, 'id'>) => {
    setCameraId(() => id);
    cameraIdRef.current = id;
    setIsPlaying(() => true);
    setSubtitle(() => name);
    setLastActivity(lastActivity);
  };

  return (
    <Box sx={{ position: 'relative', height: '100%' }} ref={livestreamWithControlsRef}>
      <StreamDragHandle label={`${patient.firstName} ${patient.lastName}`} />
      <StreamOffMessage isPlaying={isPlaying} inactivity={inactivity} />
      <LivestreamHls
        cameraId={cameraId}
        isPlaying={isPlaying && !inactivity}
        isHighlighted={isHighlighted}
        onClick={() => onClick(patient.id)}
        isHQ={isHQ}
      />
      <RouterNetworkBadge patientId={patient.id} />
      {canReadActivityEvent ? <ActivityEventToast patientId={patient.id} /> : null}
      <StreamControls
        isPlaying={isPlaying}
        onPause={onPause}
        onPlay={onPlay}
        patientName={`${patient.firstName} ${patient.lastName}`}
        inMonitoringHours={patient.inMonitoringHours}
        patientId={patient.id}
        isHovered={isHovered}
        displayedCameraName={subtitle as string}
        isHQ={isHQ}
        setIsHQ={setIsHQ}
        cameras={patient?.cameras}
        setCamera={setCamera}
        cameraId={cameraId}
        lastActivity={lastActivity}
      />
    </Box>
  );
};

interface DragHandleProps {
  label: string;
}

/**
 * Top-edge strip rendered only when the Streams layout is in edit mode. RGL's
 * `draggableHandle` selector targets the `.layout-drag-handle` class so the
 * tile only moves when grabbed here, not when clicking the live video.
 */
const StreamDragHandle: FC<DragHandleProps> = ({ label }) => {
  const editing = useSelector(useMemo(() => isLayoutEditing('streams'), []));
  if (!editing) return null;
  return (
    <Box
      className="layout-drag-handle"
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9,
        height: 28,
        px: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'move',
        bgcolor: (t) => `${t.palette.primary.main}cc`,
        color: 'primary.contrastText',
        userSelect: 'none',
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>{label}</Typography>
    </Box>
  );
};

export default StreamContainer;
